import { ApiError, type Api } from "../api/client.js";
import { MeView, type MeDestination, type MeState, type PreferenceKey } from "../ui/me.js";
import type { AimHandlers } from "../ui/aim.js";
import { readAloudSheet } from "../ui/read-aloud-sheet.js";
import { openSheet, type SheetHandle } from "../ui/sheet.js";
import type { Tab } from "../ui/tabs.js";
import type { ReadAloudSetting } from "./read-aloud-setting.js";
import { kycRow } from "./kyc-model.js";

/**
 * Me's controller: the session's own read (`GET /v1/session`: name, passkeys, the
 * switches as the bank keeps them), the switches written back (`PATCH
 * /v1/customer/preferences`), sign out (`POST /v1/session/logout`), and judge
 * mode — a per-session switch, shown only where `/healthz` says demo mode.
 */
export interface MeDeps {
  readonly api: Api;
  readonly name: string;
  readonly initials: string;
  readonly judge: () => boolean;
  readonly onJudge: (on: boolean) => void;
  readonly onLargeType: (on: boolean) => void;
  readonly onSignedOut: (why: "chosen" | "ended") => void;
  /** DCTA-111: Me → 17 Payees, 19 Security. */
  readonly onOpen: (where: MeDestination) => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
  /** DCTA-162 F29: the Read aloud choice, shared with the chat; its sheet opens over Me. */
  readonly readAloud: ReadAloudSetting;
  readonly host: HTMLElement;
  readonly reduced: () => boolean;
  /** DCTA-176 (F48): "Your identity check" opens the refresh over Me. */
  readonly onKyc?: () => void;
  /** DCTA-216 (W11): Help › How it works opens the first-run walkthrough over Me. */
  readonly onHowItWorks?: () => void;
}

export class MeScreen {
  readonly view: MeView;
  readonly #d: MeDeps;
  #state: MeState | undefined;
  #leaving = false;
  #sheet: SheetHandle | undefined;

  constructor(deps: MeDeps) {
    this.#d = deps;
    this.view = new MeView({
      onSwitch: (key, on) => void this.#write(key, on),
      onJudge: (on) => deps.onJudge(on),
      onSignOut: () => void this.#signOut(),
      onOpen: deps.onOpen,
      onReadAloud: () => this.#openReadAloud(),
      onTab: deps.onTab,
      assistant: deps.assistant,
      ...(deps.onKyc === undefined ? {} : { onKyc: deps.onKyc }),
      ...(deps.onHowItWorks === undefined ? {} : { onHowItWorks: deps.onHowItWorks }),
    });
  }

  async refresh(): Promise<void> {
    try {
      const [session, health] = await Promise.all([this.#d.api.session(), this.#d.api.health().catch(() => ({ status: "unknown" }))]);
      // DCTA-162: what the bank keeps now (another device may have chosen; B43 may have landed).
      if (session.preferences !== undefined) this.#d.readAloud.load(session.preferences);
      this.#state = {
        name: session.display_name || this.#d.name,
        initials: this.#d.initials,
        passkeys: session.passkeys,
        preferences: session.preferences ?? {
          large_type: session.accessibility_profile === "large_type",
          read_aloud: false,
          notifications: true,
        },
        readAloud: this.#d.readAloud.mode,
        demoMode: "demo_mode" in health && health.demo_mode === true,
        judge: this.#d.judge(),
        // DCTA-176 (F48): from this very read of the session — the row goes once the bank holds the check as up to date.
        kyc: kycRow(session.kyc, "demo_mode" in health && health.demo_mode === true),
      };
      this.view.show(this.#state);
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") this.#d.onSignedOut("ended");
    }
  }

  async #write(key: PreferenceKey, on: boolean): Promise<void> {
    if (key === "large_type") this.#d.onLargeType(on);
    try {
      await this.#d.api.preferences({ [key]: on });
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut("ended");
        return;
      }
      if (key === "large_type") this.#d.onLargeType(!on);
      this.view.revert(key, !on);
    }
  }

  /**
   * DCTA-162 F29: the Read aloud sheet over Me. A choice is kept at once and written to the bank (as its switch until
   * B43, `ReadAloudSetting`); the row follows. Not kept: the choice goes back on the sheet and the row, one line says so.
   */
  #openReadAloud(): void {
    if (this.#sheet !== undefined) return;
    const setting = this.#d.readAloud;
    const close = (): void => {
      const sheet = this.#sheet;
      this.#sheet = undefined;
      void sheet?.close();
    };
    const handle = readAloudSheet(setting.mode, {
      onChoose: (mode) => {
        this.view.readAloud(mode);
        void setting
          .choose(mode, async (patch) => {
            try {
              await this.#d.api.preferences(patch);
            } catch (error) {
              if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
                close();
                this.#d.onSignedOut("ended");
              }
              throw error;
            }
          })
          .then((kept) => {
            if (kept) return;
            handle.failed(setting.mode);
            this.view.readAloud(setting.mode);
          });
      },
      onDone: close,
    });
    this.#sheet = openSheet(this.#d.host, handle.sheet, { behind: this.view.parts, reduced: this.#d.reduced(), onDismiss: close });
  }

  async #signOut(): Promise<void> {
    if (this.#leaving) return;
    this.#leaving = true;
    // Whatever the bank answers, this device leaves the session: a failed call
    // must not leave the customer looking at a screen that is no longer theirs.
    await this.#d.api.logout().catch(() => undefined);
    this.#d.onSignedOut("chosen");
  }
}
